<template>
  <div class="musubi-page musubi-page--tracking">
    <header class="musubi-page__head">
      <h1>Tracking &amp; attribution</h1>
      <p class="text-muted">Install the first-party pixel, confirm it's reporting, and build tagged links that
        flow straight into your attribution reports.</p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <div class="tk-grid">
      <!-- ── Embed snippet ─────────────────────────────────────────── -->
      <MusubiSectionCard title="Install the snippet" subtitle="paste once, before &lt;/body&gt;" icon="code">
        <p class="tk-lead">
          Add this to every page you want to measure — your own site, a landing page, anywhere.
          It records <b>pageviews</b>, the <b>UTM tags</b> a visitor arrived with, and the
          <b>referring site</b>, then links that browsing to a lead the moment they fill in a form.
          It stores nothing until the visitor consents.
        </p>

        <div v-if="snippet.snippet_html" class="tk-code">
          <code>{{ snippet.snippet_html }}</code>
          <button type="button" class="tk-copy" @click="copyValue(snippet.snippet_html)">
            <CygnusIcon name="file" /> Copy
          </button>
        </div>
        <p v-else class="text-muted small py-2">Loading your snippet…</p>

        <p v-if="snippet.is_host_tenant" class="tk-note">
          <CygnusIcon name="info-circle" />
          You're on the platform workspace — this snippet tags data to workspace <b>#{{ snippet.tenant_id }}</b>.
          Customer workspaces get their own <code>?t=</code> automatically.
        </p>
      </MusubiSectionCard>

      <!-- ── Live verification ─────────────────────────────────────── -->
      <MusubiSectionCard title="Is it working?" icon="broadcast">
        <div v-if="statusLoading" class="text-muted small py-3">Checking…</div>

        <div v-else-if="status.has_data" class="tk-live is-ok">
          <CygnusIcon name="check-circle" />
          <div>
            <b>Receiving data</b>
            <span>Last pageview {{ fromNow(status.last_seen) }}<template v-if="status.last_host"> from {{ status.last_host }}</template>.</span>
            <span class="tk-live__sub">{{ fmt(status.session_count) }} tracked {{ status.session_count === 1 ? 'session' : 'sessions' }} so far.</span>
          </div>
        </div>

        <div v-else class="tk-live is-wait">
          <CygnusIcon name="clock" />
          <div>
            <b>No data yet</b>
            <span>The snippet hasn't phoned home. Paste it, load a page, accept the consent banner,
              then refresh this check.</span>
          </div>
        </div>

        <button type="button" class="tk-refresh" @click="loadStatus" :disabled="statusLoading">
          <CygnusIcon name="refresh" /> Re-check
        </button>
      </MusubiSectionCard>
    </div>

    <!-- ── Consent posture ──────────────────────────────────────────── -->
    <MusubiSectionCard title="Consent" subtitle="honest by default" icon="user-shield" class="tk-consent">
      <p class="tk-lead">
        Out of the box the snippet shows its own consent banner and tracks nothing until the visitor
        accepts — and it silently honours a browser Global Privacy Control opt-out. On your own hosted
        landing pages this is already wired up, so nothing changes there.
      </p>
      <p class="tk-lead">
        Already running your own cookie manager (Cookiebot, OneTrust, a custom banner)? Add
        <code>data-consent="wait"</code> to the tag to suppress our banner, then call the consent API
        from your banner's Accept / Reject handlers:
      </p>
      <div class="tk-code tk-code--multi">
        <code>&lt;script src="{{ snippet.snippet_url || '/pixel.js?t=…' }}" data-consent="wait" defer&gt;&lt;/script&gt;

<!-- when the visitor accepts -->
window.yorlinTrack('consent', 'granted');
<!-- when the visitor declines -->
window.yorlinTrack('consent', 'denied');</code>
        <button type="button" class="tk-copy" @click="copyValue(consentSnippet)">
          <CygnusIcon name="file" /> Copy
        </button>
      </div>
    </MusubiSectionCard>

    <!-- ── UTM link builder ─────────────────────────────────────────── -->
    <MusubiSectionCard title="UTM link builder" subtitle="tag a link so attribution can read it"
                       icon="link" class="tk-builder">
      <div class="tk-fields">
        <div class="tk-field tk-field--wide">
          <label>Destination URL</label>
          <input v-model.trim="b.url" type="url" placeholder="https://example.com/landing" />
        </div>

        <div class="tk-field">
          <label>Campaign</label>
          <select v-model="b.campaign">
            <option value="">— none —</option>
            <optgroup v-if="campaignOpts.length" label="Campaigns">
              <option v-for="c in campaignOpts" :key="'c'+c.id" :value="c.utm_campaign">{{ c.name }}</option>
            </optgroup>
            <optgroup v-if="pipelineOpts.length" label="Pipelines">
              <option v-for="p in pipelineOpts" :key="'p'+p.id" :value="p.utm_campaign">{{ p.name }}</option>
            </optgroup>
          </select>
          <span class="tk-hint">Sets <code>utm_campaign</code> — the key your attribution matches on first.</span>
        </div>

        <div class="tk-field">
          <label>Source</label>
          <select v-model="b.source">
            <option v-for="s in sourcePresets" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>

        <div class="tk-field">
          <label>Medium</label>
          <select v-model="b.medium">
            <option v-for="m in mediumPresets" :key="m" :value="m">{{ m }}</option>
          </select>
        </div>
      </div>

      <div v-if="composedUrl" class="tk-code tk-code--out">
        <code>{{ composedUrl }}</code>
        <button type="button" class="tk-copy" @click="copyValue(composedUrl)">
          <CygnusIcon name="file" /> Copy
        </button>
      </div>
      <p v-else class="text-muted small py-2">Enter a destination URL to compose a tagged link.</p>
    </MusubiSectionCard>

    <!-- Copy affordances: transient note + offscreen fallback input. -->
    <div aria-live="polite" class="tk-copied" v-show="copiedNote">{{ copiedNote }}</div>
    <input ref="fallbackInput" class="tk-offscreen" tabindex="-1" aria-hidden="true" />
  </div>
</template>

<script>
/**
 * AdminTracking — Tracking & attribution setup surface. Reads three read-only
 * endpoints (snippet / status / campaigns) under /api/v1/admin/tracking. The
 * embed snippet carries the acting tenant's ?t=; the UTM builder emits the exact
 * utm_campaign slug the backend's AttributionResolver matches on (shared with
 * the auto-UTM email stamp).
 */
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: 'AdminTracking',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      snippet: { snippet_html: '', snippet_url: '', tenant_id: 0, is_host_tenant: false, utm_source: 'musubi' },
      status: { has_data: false, session_count: 0, last_seen: null, last_host: null },
      picker: [],
      b: { url: '', campaign: '', source: 'musubi', medium: 'email' },
      sourcePresets: ['musubi', 'email', 'newsletter', 'linkedin', 'twitter', 'reddit', 'referral'],
      mediumPresets: ['email', 'social', 'cpc', 'referral', 'organic'],
      loading: false,
      statusLoading: false,
      error: null,
      copiedNote: '',
      copyTimer: null,
    };
  },
  mounted() {
    this.loadSnippet();
    this.loadStatus();
    this.loadCampaigns();
  },
  computed: {
    campaignOpts() { return this.picker.filter((x) => x.type === 'campaign'); },
    pipelineOpts() { return this.picker.filter((x) => x.type === 'pipeline'); },
    consentSnippet() {
      const url = this.snippet.snippet_url || '/pixel.js?t=…';
      return `<script src="${url}" data-consent="wait" defer><\/script>\n`
        + `// when the visitor accepts\n`
        + `window.yorlinTrack('consent', 'granted');\n`
        + `// when the visitor declines\n`
        + `window.yorlinTrack('consent', 'denied');`;
    },
    composedUrl() {
      const dest = (this.b.url || '').trim();
      if (!dest || !/^https?:\/\//i.test(dest)) return '';
      let hash = '';
      let base = dest;
      const hi = base.indexOf('#');
      if (hi !== -1) { hash = base.slice(hi); base = base.slice(0, hi); }
      const parts = [];
      const add = (k, v) => { if (v) parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(v)); };
      add('utm_source', this.b.source);
      add('utm_medium', this.b.medium);
      add('utm_campaign', this.b.campaign);
      if (!parts.length) return base + hash;
      const sep = base.includes('?') ? '&' : '?';
      return base + sep + parts.join('&') + hash;
    },
  },
  methods: {
    async loadSnippet() {
      try {
        const res = await this.api.get('/api/v1/admin/tracking/snippet');
        const p = res?.data?.data ?? res?.data ?? res;
        this.snippet = { ...this.snippet, ...(p || {}) };
        if (p?.utm_source) this.b.source = p.utm_source;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load your snippet.';
      }
    },
    async loadStatus() {
      this.statusLoading = true;
      try {
        const res = await this.api.get('/api/v1/admin/tracking/status');
        const p = res?.data?.data ?? res?.data ?? res;
        this.status = { has_data: false, session_count: 0, last_seen: null, last_host: null, ...(p || {}) };
      } catch (e) { /* non-fatal — leave the empty state */ }
      this.statusLoading = false;
    },
    async loadCampaigns() {
      try {
        const res = await this.api.get('/api/v1/admin/tracking/campaigns');
        const p = res?.data?.data ?? res?.data ?? res;
        this.picker = (p?.campaigns) || [];
      } catch (e) { /* non-fatal — builder still works with a free-typed campaign */ }
    },
    async copyValue(value) {
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value); copied = true;
        }
      } catch (e) { /* fall through */ }
      if (!copied) {
        const input = this.$refs.fallbackInput;
        if (input) { input.value = value; input.focus(); input.select(); try { document.execCommand('copy'); } catch (e) { /* ignore */ } }
      }
      this.copiedNote = 'Copied to clipboard.';
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => { this.copiedNote = ''; }, 1400);
    },
    fmt(n) { return (Number(n) || 0).toLocaleString(); },
    fromNow(ts) {
      if (!ts) return 'just now';
      const secs = Date.now() - ts * 1000;
      if (secs < 60000) return 'moments ago';
      const mins = Math.round(secs / 60000);
      if (mins < 60) return `${mins} min ago`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return `${hrs} h ago`;
      return `${Math.round(hrs / 24)} d ago`;
    },
  },
};
</script>

<style scoped>
.tk-grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 16px; align-items: start; margin: 18px 0 16px; }
.tk-lead { color: var(--m-text-secondary); font-size: 0.9rem; line-height: 1.55; margin: 0 0 12px; }
.tk-lead b { color: var(--m-text-primary); }

.tk-code { position: relative; background: var(--m-surface-sunken, var(--m-bg)); border: 1px solid var(--m-border); border-radius: 8px; padding: 12px 88px 12px 14px; margin: 6px 0 4px; }
.tk-code code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.8rem; color: var(--m-text-primary); white-space: pre-wrap; word-break: break-all; display: block; }
.tk-code--multi code, .tk-code--out code { white-space: pre-wrap; }
.tk-copy { position: absolute; top: 10px; right: 10px; background: var(--m-accent); color: #fff; border: 0; border-radius: 6px; padding: 6px 12px; font: inherit; font-size: 0.78rem; font-weight: 600; cursor: pointer; }
.tk-copy:hover { filter: brightness(0.94); }

.tk-note { display: flex; gap: 8px; align-items: flex-start; background: var(--m-warning-soft, rgba(234,179,8,0.1)); border-radius: 6px; padding: 8px 12px; font-size: 0.8rem; color: var(--m-text-secondary); margin: 8px 0 0; }
.tk-note i { color: var(--m-warning, #ca8a04); margin-top: 2px; }
.tk-note code { font-size: 0.76rem; }

.tk-live { display: flex; gap: 12px; align-items: flex-start; padding: 6px 0 4px; }
.tk-live > i { font-size: 1.4rem; margin-top: 2px; }
.tk-live.is-ok > i { color: var(--m-success, #059669); }
.tk-live.is-wait > i { color: var(--m-text-tertiary); }
.tk-live b { display: block; font-size: 0.95rem; color: var(--m-text-primary); }
.tk-live span { display: block; font-size: 0.82rem; color: var(--m-text-secondary); margin-top: 2px; }
.tk-live__sub { color: var(--m-text-tertiary) !important; }
.tk-refresh { margin-top: 12px; background: transparent; color: var(--m-accent); border: 1px solid var(--m-border); border-radius: 6px; padding: 6px 14px; font: inherit; font-size: 0.8rem; font-weight: 600; cursor: pointer; }
.tk-refresh:hover:not(:disabled) { border-color: var(--m-accent); }
.tk-refresh:disabled { opacity: 0.5; cursor: default; }

.tk-consent { margin-bottom: 16px; }
.tk-consent code, .tk-builder code { font-size: 0.78rem; }

.tk-builder .tk-fields { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; margin: 4px 0 14px; }
.tk-field { display: flex; flex-direction: column; gap: 5px; }
.tk-field--wide { grid-column: 1 / -1; }
.tk-field label { font-size: 0.78rem; font-weight: 650; color: var(--m-text-secondary); }
.tk-field input, .tk-field select { padding: 9px 11px; border: 1px solid var(--m-border); border-radius: 8px; font: inherit; font-size: 0.85rem; background: var(--m-bg); color: var(--m-text-primary); }
.tk-field input:focus, .tk-field select:focus { outline: none; border-color: var(--m-accent); }
.tk-hint { font-size: 0.72rem; color: var(--m-text-tertiary); }
.tk-hint code { font-size: 0.7rem; }

.tk-copied { position: fixed; bottom: 22px; left: 50%; transform: translateX(-50%); background: var(--m-text-primary); color: var(--m-bg); padding: 8px 18px; border-radius: 999px; font-size: 0.82rem; font-weight: 600; z-index: 3000; box-shadow: 0 4px 16px rgba(0,0,0,0.2); }
.tk-offscreen { position: absolute; left: -9999px; width: 1px; height: 1px; }

@media (max-width: 1024px) { .tk-grid { grid-template-columns: 1fr; } }
@media (max-width: 640px) { .tk-builder .tk-fields { grid-template-columns: 1fr; } }
</style>
